import { PropsWithChildren, ReactNode } from 'react';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { printUnclip } from '../helpers';

type SidebarLayoutProps = PropsWithChildren<{
	sidebar: ReactNode;
	sidebarWidth?: number;
	isSidebarOpen?: boolean;
	right?: boolean;
	/** Fill the parent and scroll the sidebar and main column independently. */
	fill?: boolean;
}>;

const Root = styled(Flex)<{ $fill?: boolean }>`
	align-items: stretch;
	flex-grow: 1;
	min-width: 0;
	width: 100%;

	${(props) => props.$fill && printUnclip}
`;

const Sidebar = styled(Box)<{ $right?: boolean }>`
	flex-shrink: 0;
	flex-grow: 0;

	${({ theme, $right }) => {
		const border = `1px solid ${theme.tokens.border.subtle}`;

		return $right
			? css`
					border-left: ${border};
				`
			: css`
					border-right: ${border};
				`;
	}}

	@media print {
		display: none !important;
	}
`;

const MainColumn = styled(Flex)<{ $fill?: boolean }>`
	flex-shrink: 1;
	flex-grow: 1;
	flex-direction: column;
	min-width: 0;

	${(props) =>
		props.$fill &&
		css`
			overscroll-behavior: contain;
		`}

	${printUnclip}
`;

export function SidebarLayout(props: SidebarLayoutProps) {
	const right = Boolean(props.right);
	const fill = Boolean(props.fill);
	const sidebarWidth = props.sidebarWidth ?? (right ? 340 : 270);

	const mobileSidebarDisplay =
		props.isSidebarOpen === undefined ? 'block' : props.isSidebarOpen ? 'block' : 'none';

	const stackedDirection = right ? 'column-reverse' : 'column';
	const splitDirection = right ? 'row-reverse' : 'row';

	// Stacked layouts scroll as one page, so only the split layout clips and
	// hands each pane its own scroll area.
	const rootFill = fill
		? {
				flex: '1 1 auto',
				height: ['auto', 'auto', '100%'],
				overflow: ['visible', 'visible', 'hidden'],
			}
		: undefined;

	const paneFill = fill ? { minHeight: 0, overflow: ['visible', 'visible', 'auto'] } : undefined;

	return (
		<Root
			$fill={fill}
			alignItems="stretch"
			flexDirection={[stackedDirection, stackedDirection, splitDirection]}
			flexGrow={1}
			minWidth={0}
			width="100%"
			{...rootFill}
		>
			<Sidebar
				display={[mobileSidebarDisplay, mobileSidebarDisplay, 'block']}
				$right={right}
				width={['100%', '100%', sidebarWidth]}
				{...paneFill}
			>
				{props.sidebar}
			</Sidebar>
			<MainColumn $fill={fill} {...paneFill}>
				{props.children}
			</MainColumn>
		</Root>
	);
}
